import { z } from 'zod';
import {
  AXES,
  CRITERES_ELIMINATOIRES,
  calculerNote,
  scoresSchema,
  type Poids,
  type Scores,
} from './bareme.js';
import type { StatutCandidature } from './candidature.js';
import type { GroupeBareme } from './familles.js';

/** Type PostgreSQL `type_contact`. */
export const TYPES_CONTACT = ['telephonique', 'presentiel'] as const;
export const typeContactSchema = z.enum(TYPES_CONTACT);
export type TypeContact = z.infer<typeof typeContactSchema>;

export const ouvertureContactSchema = z.object({ type: typeContactSchema }).strict();

/**
 * Trame de l'entretien présentiel (docs/UI.md § Écran 3). Les observations
 * libres vivent dans `contacts.observations`, commune aux deux types.
 * L'aide rappelle ce qui ne se demande pas (CLAUDE.md § Interdits).
 */
export const RUBRIQUES_TRAME = [
  { cle: 'identite', libelle: 'Identité et présentation', aide: 'Pièce d’identité, présentation, ponctualité.' },
  { cle: 'parcours', libelle: 'Parcours', aide: 'Postes occupés, durées, motifs de départ.' },
  { cle: 'situation', libelle: 'Situation actuelle', aide: 'Emploi en cours, préavis. Jamais la situation familiale ni la santé.' },
  { cle: 'conditions', libelle: 'Conditions', aide: 'Horaires acceptés, nuit et week-end, trajet, salaire attendu.' },
  { cle: 'competences', libelle: 'Compétences métier', aide: 'Questions techniques propres au poste.' },
  { cle: 'mise_en_situation', libelle: 'Mise en situation', aide: 'Exercice pratique et ce qui a été observé.' },
  { cle: 'conformite', libelle: 'Conformité', aide: 'Justificatifs annoncés, référence vérifiable, règles d’hygiène.' },
] as const;
export type RubriqueTrame = (typeof RUBRIQUES_TRAME)[number]['cle'];
export type Reponses = Partial<Record<RubriqueTrame, string>>;

export const reponsesSchema = z
  .object(
    Object.fromEntries(RUBRIQUES_TRAME.map((r) => [r.cle, z.string().max(4000).optional()])) as Record<
      RubriqueTrame,
      z.ZodOptional<z.ZodString>
    >,
  )
  .strict();

const instant = z.string().datetime({ offset: true });

/**
 * Identifiant tiré par le téléphone pour une clôture ou une évaluation : la
 * même opération renvoyée après une réponse perdue est reconnue et sans effet.
 */
const identifiantEnvoi = z.string().uuid();

/**
 * Saisie du panneau de contact. Un champ absent reste inchangé, `null`
 * l'efface. Le schéma est strict : aucun champ hors liste — âge, situation
 * familiale ou autre critère proscrit — ne peut entrer par l'API.
 */
export const majContactSchema = z
  .object({
    joignable: z.boolean().nullable(),
    disponibleDeSuite: z.boolean().nullable(),
    interesse: z.boolean().nullable(),
    rappelLe: instant.nullable(),
    rdvLe: instant.nullable(),
    rdvLieuId: z.string().uuid().nullable(),
    observations: z.string().max(4000).nullable(),
    potentiel: z.number().int().min(0).max(5).nullable(),
    reponses: reponsesSchema,
  })
  .partial()
  .strict();
export type MajContact = z.infer<typeof majContactSchema>;

/** Champs propres à l'appel : la pastille se pose au téléphone, jamais en présentiel. */
export const CHAMPS_TELEPHONE = [
  'joignable',
  'disponibleDeSuite',
  'interesse',
  'rappelLe',
  'rdvLe',
  'rdvLieuId',
  'potentiel',
] as const satisfies ReadonlyArray<keyof MajContact>;

/**
 * Un appel ne se clôture qu'une fois son issue connue, et la pastille posée
 * si le candidat a répondu : c'est tout l'objet du contact téléphonique
 * (docs/BAREME.md § 1).
 */
export function motifRefusCloture(contact: {
  type: TypeContact;
  joignable: boolean | null;
  potentiel: number | null;
  rdvLe: string | Date | null;
}): string | null {
  if (contact.type !== 'telephonique') return null;
  if (contact.joignable === null) return 'Indiquer si le candidat a été joint.';
  if (contact.joignable && contact.potentiel === null) {
    return 'Poser la pastille de potentiel avant de clôturer l’appel.';
  }
  if (!contact.joignable && contact.rdvLe) {
    return 'Un rendez-vous suppose d’avoir joint le candidat.';
  }
  return null;
}

/**
 * Corps de la clôture, facultatif : sans heure, le contact se clôt à l'heure
 * du serveur ; avec, à l'heure de l'appui sur le téléphone, bornée par l'API
 * (`bornerHorodatage`, packages/shared/src/saisie-differee.ts).
 */
export const clotureContactSchema = z
  .object({ envoiId: identifiantEnvoi.optional(), termineLe: instant.optional() })
  .strict();
export type ClotureContact = z.infer<typeof clotureContactSchema>;

/** Rang dans la chaîne nominale (docs/MODELE_DONNEES.md § Statuts). */
const RANG: Partial<Record<StatutCandidature, number>> = {
  nouveau: 0,
  a_contacter: 1,
  contacte: 2,
  rdv_planifie: 3,
  entretien_realise: 4,
  rapport_remis: 5,
  justificatifs_recus: 6,
};

/**
 * Statut après un appel clôturé. Le dossier avance, il ne recule jamais ; un
 * dossier écarté, désisté ou embauché ne bouge pas : c'est une décision
 * humaine. Le vivier repart dans la chaîne dès qu'on rappelle le candidat.
 */
export function statutApresAppel(
  actuel: StatutCandidature,
  appel: { joignable: boolean; rdvLe: string | Date | null },
): StatutCandidature {
  const rangActuel = actuel === 'vivier' ? -1 : RANG[actuel];
  if (rangActuel === undefined) return actuel;
  const cible: StatutCandidature = !appel.joignable
    ? 'a_contacter'
    : appel.rdvLe
      ? 'rdv_planifie'
      : 'contacte';
  return RANG[cible]! > rangActuel ? cible : actuel;
}

/** Statut après une évaluation : un critère éliminatoire écarte le dossier. */
export function statutApresEvaluation(
  actuel: StatutCandidature,
  eliminatoire: boolean,
): StatutCandidature {
  if (actuel === 'embauche') return actuel;
  if (eliminatoire) return 'ecarte';
  const rangActuel = actuel === 'vivier' ? -1 : RANG[actuel];
  if (rangActuel === undefined) return actuel;
  return rangActuel < RANG.entretien_realise! ? 'entretien_realise' : actuel;
}

export type CleEliminatoire = (typeof CRITERES_ELIMINATOIRES)[number]['cle'];
const CLES_ELIMINATOIRES = CRITERES_ELIMINATOIRES.map((c) => c.cle) as [
  CleEliminatoire,
  ...CleEliminatoire[],
];

export const evaluationSchema = z
  .object({
    scores: scoresSchema,
    eliminatoire: z.boolean(),
    motifEliminatoire: z.enum(CLES_ELIMINATOIRES).nullable().optional(),
    precisionEliminatoire: z.string().max(1000).nullable().optional(),
    /** Absente ou égale à la note calculée : pas de forçage. */
    noteFinale: z.number().min(0).max(5).nullable().optional(),
    justification: z.string().max(2000).nullable().optional(),
    // Saisie différée : tous facultatifs, pour qu'une PWA restée sur l'ancien
    // code continue d'évaluer. Le barème est comparé par identifiant ; sa
    // version ne sert qu'au message.
    envoiId: identifiantEnvoi.optional(),
    evalueeLe: instant.optional(),
    baremeId: z.string().uuid().optional(),
    baremeVersion: z.number().int().min(1).optional(),
  })
  .strict()
  .refine((e) => (e.baremeId === undefined) === (e.baremeVersion === undefined), {
    message: 'baremeId et baremeVersion vont ensemble',
    path: ['baremeId'],
  });
export type EntreeEvaluation = z.infer<typeof evaluationSchema>;
/** La notation seule, sans les champs d'envoi. */
export type ChampsNotation = Pick<
  EntreeEvaluation,
  'scores' | 'eliminatoire' | 'motifEliminatoire' | 'precisionEliminatoire' | 'noteFinale' | 'justification'
>;

/** Longueur minimale d'une justification de forçage : « motivée » veut dire une phrase. */
export const JUSTIFICATION_MINIMUM = 10;

export type IssueEvaluation =
  | {
      valide: true;
      noteCalculee: number;
      noteFinale: number;
      forcee: boolean;
      motifEliminatoire: string | null;
      justification: string | null;
    }
  | { valide: false; erreur: string };

function enFrancais(note: number): string {
  return note.toFixed(1).replace('.', ',');
}

/**
 * Règles de docs/BAREME.md § 3 et § 4 : un critère éliminatoire force la note
 * à 0 avec motif obligatoire ; hors éliminatoire, la note finale est la note
 * calculée, sauf forçage justifié.
 */
export function resoudreEvaluation(entree: ChampsNotation, poids: Poids): IssueEvaluation {
  const scores: Scores = {};
  for (const axe of AXES) {
    const score = entree.scores[axe];
    if (score !== undefined) scores[axe] = score;
  }
  const noteCalculee = calculerNote(scores, poids);
  const demandee =
    entree.noteFinale === undefined || entree.noteFinale === null
      ? null
      : Math.round(entree.noteFinale * 10) / 10;

  if (entree.eliminatoire) {
    const critere = CRITERES_ELIMINATOIRES.find((c) => c.cle === entree.motifEliminatoire);
    if (!critere) return { valide: false, erreur: 'Un critère éliminatoire exige son motif.' };
    if (demandee !== null && demandee !== 0) {
      return { valide: false, erreur: 'Un critère éliminatoire force la note à 0 : elle ne se force pas.' };
    }
    const precision = entree.precisionEliminatoire?.trim();
    return {
      valide: true,
      noteCalculee,
      noteFinale: 0,
      forcee: false,
      motifEliminatoire: precision ? `${critere.libelle} — ${precision}` : critere.libelle,
      justification: null,
    };
  }

  if (!AXES.some((axe) => scores[axe] !== undefined && poids[axe] > 0)) {
    return {
      valide: false,
      erreur: 'Aucun axe pondéré n’est noté : la note ne peut pas être calculée.',
    };
  }

  const noteFinale = demandee ?? noteCalculee;
  const forcee = noteFinale !== noteCalculee;
  const justification = entree.justification?.trim() ?? '';
  if (forcee && justification.length < JUSTIFICATION_MINIMUM) {
    return {
      valide: false,
      erreur:
        `La note calculée est ${enFrancais(noteCalculee)} : la forcer à ${enFrancais(noteFinale)} ` +
        `exige une justification (${JUSTIFICATION_MINIMUM} caractères au moins).`,
    };
  }
  return {
    valide: true,
    noteCalculee,
    noteFinale,
    forcee,
    motifEliminatoire: null,
    justification: forcee ? justification : null,
  };
}

/** Barème qui s'applique à une candidature, servi au panneau présentiel. */
export interface BaremeApplicable {
  id: string;
  version: number;
  familleCode: string;
  familleLibelle: string;
  groupe: GroupeBareme | null;
  poids: Poids;
  /**
   * Vrai si le barème est figé sur l'annonce (docs/MODELE_DONNEES.md règle 5) ;
   * faux quand l'annonce n'est pas encore validée et que le barème actif de la
   * famille s'applique.
   */
  figeSurAnnonce: boolean;
}

export interface EvaluationDetail {
  id: string;
  baremeId: string;
  baremeVersion: number;
  poids: Poids;
  scores: Scores;
  eliminatoire: boolean;
  motifEliminatoire: string | null;
  noteCalculee: number;
  noteFinale: number;
  justificationEcart: string | null;
  evalueePar: string;
  evalueeLe: string;
  /** Identifiant d'envoi du téléphone ; null pour une évaluation saisie en ligne par l'ancien code. */
  envoiId: string | null;
}

/**
 * Un contact tel que le servent l'historique et le panneau. Pour le rôle
 * `lecteur`, observations et trame sont retirées : texte libre, elles
 * portent volontiers des coordonnées.
 */
export interface ContactDetail {
  id: string;
  candidatureId: string;
  type: TypeContact;
  debuteLe: string;
  termineLe: string | null;
  auteurId: string;
  auteur: string;
  joignable: boolean | null;
  disponibleDeSuite: boolean | null;
  interesse: boolean | null;
  rappelLe: string | null;
  rdvLe: string | null;
  rdvLieuId: string | null;
  rdvLieu: string | null;
  observations: string | null;
  reponses: Reponses;
  potentiel: number | null;
  /** Identifiant d'envoi de la clôture : le téléphone reconnaît ainsi une clôture déjà reçue. */
  clotureEnvoiId: string | null;
  evaluation: EvaluationDetail | null;
}
